import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  Switch,
  FormControlLabel,
  Alert,
  Collapse,
  Button,
  TextField,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RssFeedIcon from "@mui/icons-material/RssFeed";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { toast } from "react-toastify";
import api from "../../../../conf/api";

// Same list RephrasedContentReview.js (Brand Publish's AI step) offers —
// kept in sync so "Rephrase" here behaves identically to the existing flow.
const LANGUAGES = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese", "Dutch",
  "Russian", "Chinese", "Japanese", "Korean", "Arabic", "Hindi", "Turkish",
  "Polish", "Swedish", "Norwegian", "Danish", "Finnish", "Greek", "Czech",
  "Romanian", "Hungarian", "Thai", "Vietnamese", "Indonesian", "Malay",
  "Filipino", "Hebrew", "Ukrainian",
];

// Matches admin_portal/video_lifecycle/mrss/public_feed.py's
// `_append_description_footer` field order/labels exactly — informational
// only. Admin Portal appends these lines to the description itself, at
// serve time, regardless of what CreatorVault sends — the actual line text
// (e.g. the filmer's name) depends on Admin Portal-side data (second
// signer/uploader) CreatorVault doesn't have, so this only tells the
// reviewer WHICH lines a feed adds, not their exact resolved values.
const DESCRIPTION_FIELD_LABELS = [
  ["name", "Filmer name"],
  ["location", "Location"],
  ["filmed_on", "Filmed on date"],
  ["wgid", "WooGlobe Ref (WGID)"],
  ["license_signature", "Licensing contact line"],
  ["wooglobe_signature", "WooGlobe social links"],
];

/**
 * Step 4 — review the target feeds x videos matrix, edit the title/
 * description/tags actually being sent, PER (video, feed) pair — a feed's
 * own "Use Title 2" / description-fields configuration (Admin Portal's
 * MrssInfo, fetched onto each feed object in Step 1) can differ from
 * another feed's for the same video, so these can't be one shared override
 * per video (mirrors StepVideoSource.js's per-pair independence). Optionally
 * AI-rephrase/translate them via the same /mrss/rephrase/ endpoint the Brand
 * Publish wizard uses, and choose Force Push before submitting. The actual
 * Submit button lives in the wizard shell (index.js), matching the Brand
 * Publish page's NavButtons convention.
 */
/** How many rephrase requests "Rephrase all" keeps in flight at once. */
const REPHRASE_ALL_CONCURRENCY = 3;

/** Where an edited rephrase prompt is remembered, in this browser. */
const PROMPT_STORAGE_KEY = "cv:push-ap:rephrase-prompt";

const readSavedPrompt = () => {
  try {
    return localStorage.getItem(PROMPT_STORAGE_KEY);
  } catch (_) {
    return null;
  }
};

const writeSavedPrompt = (value) => {
  try {
    if (value) localStorage.setItem(PROMPT_STORAGE_KEY, value);
    else localStorage.removeItem(PROMPT_STORAGE_KEY);
  } catch (_) {
    // Private mode or blocked storage: the prompt still applies to this page.
  }
};

export default function Step4ReviewSubmit({
  selectedGroups,
  selectedFeedIds,
  groupFeeds,
  selectedVideos,
  overrides,
  onOverrideChange,
  forcePush,
  onForcePushChange,
}) {
  const [expanded, setExpanded] = useState({});
  const [language, setLanguage] = useState({}); // { "unique_key::feed_id": "English" }
  const [rephrasing, setRephrasing] = useState({}); // { "unique_key::feed_id": bool }
  // Pairs whose last rephrase failed, so "Rephrase all" can point at them and
  // their own Rephrase button can be used to retry.
  const [failed, setFailed] = useState({}); // { "unique_key::feed_id": error message }
  const [bulk, setBulk] = useState(null); // { done, total } while "Rephrase all" runs
  // The rephrase prompt (user request 2026-10-13): shown and editable here.
  // `defaultPrompt` is the server's own; `prompt` what is in the box. Only an
  // edited prompt is sent, so an unchanged box keeps using the server default.
  const [defaultPrompt, setDefaultPrompt] = useState(null);
  const [prompt, setPrompt] = useState(() => readSavedPrompt());
  const [promptOpen, setPromptOpen] = useState(false); // collapsed by default (user request 2026-10-14)

  useEffect(() => {
    let live = true;
    api
      .get("/mrss/rephrase/")
      .then((res) => {
        if (!live) return;
        const text = res.data?.prompt || "";
        setDefaultPrompt(text);
        setPrompt((current) => (current === null || current === undefined ? text : current));
      })
      .catch(() => {
        if (live) setDefaultPrompt("");
      });
    return () => {
      live = false;
    };
  }, []);

  const customPrompt = prompt && prompt.trim() && prompt !== defaultPrompt ? prompt : null;

  const changePrompt = (value) => {
    setPrompt(value);
    writeSavedPrompt(value && value.trim() && value !== defaultPrompt ? value : null);
  };

  const feeds = groupFeeds.filter((f) => selectedFeedIds.includes(f.id));
  const pairKey = (uniqueKey, feedId) => `${uniqueKey}::${feedId}`;

  const toggleExpanded = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  // The WooGlobe search result (/apis/videos-filter, VideoListFinderView)
  // returns a raw `tags` column (comma-separated string), not `keywords` —
  // only CreatorVault-local MRSSVideo/compilation rows use `keywords` (array).
  // Cover both shapes so this works for either source.
  const resolveTagsString = (video) => {
    if (typeof video.tags === "string" && video.tags) return video.tags;
    if (Array.isArray(video.tags)) return video.tags.join(", ");
    if (Array.isArray(video.keywords)) return video.keywords.join(", ");
    if (typeof video.keywords === "string") return video.keywords;
    return "";
  };

  // A feed configured "Use Title 2" (Admin Portal's MrssInfo.is_title_2)
  // always loses to an explicit title override there (`_resolve_title`:
  // `pub.title` wins over everything) — since CreatorVault always sends
  // SOME title, the only way to actually honor that feed setting is for
  // CreatorVault to pick title_2 itself before sending.
  const resolveDefaultTitle = (video, feed) => {
    if (feed?.is_title_2 && video.video_title_2) return video.video_title_2;
    return video.title || video.video_title || "";
  };

  // Pre-fill each (video, feed) pair's override fields with its own content
  // as soon as both are selected — the fields start populated and editable,
  // same as RephrasedContentReview.js's "original" content, rather than
  // starting blank with only a placeholder.
  useEffect(() => {
    selectedVideos.forEach((video) => {
      feeds.forEach((feed) => {
        if (overrides[video.unique_key]?.[feed.id]) return; // already fetched/edited — don't clobber
        onOverrideChange(video.unique_key, feed.id, {
          title: resolveDefaultTitle(video, feed),
          description: video.description || "",
          tags: resolveTagsString(video),
        });
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVideos, feeds.map((f) => f.id).join(","), selectedVideos.map((v) => v.unique_key).join(",")]);

  /**
   * Rephrase (or translate) one video × feed pair. True when it worked.
   * `quiet` (used by "Rephrase all") skips the per-pair toasts — one summary
   * is shown at the end instead.
   */
  const handleRephrase = async (video, feed, { quiet = false } = {}) => {
    const key = pairKey(video.unique_key, feed.id);
    const ov = overrides[video.unique_key]?.[feed.id] || {};
    const lang = language[key] || "English";
    setRephrasing((prev) => ({ ...prev, [key]: true }));
    try {
      const response = await api.post(
        "/mrss/rephrase/",
        {
          title: ov.title || resolveDefaultTitle(video, feed),
          description: ov.description || video.description || "",
          keywords: (ov.tags || resolveTagsString(video))
            .split(",")
            .map((t) => t.trim())
            .filter(Boolean),
          target_language: lang,
          // The edited prompt rephrases (English). Translations keep the
          // server's own translate prompt — a rephrase prompt there would
          // ask the model to rephrase instead of translating.
          ...(customPrompt && lang.toLowerCase() === "english" ? { custom_prompt: customPrompt } : {}),
        },
        { timeout: 6 * 60 * 1000 }
      );
      if (response.data.success) {
        onOverrideChange(video.unique_key, feed.id, {
          title: response.data.title,
          description: response.data.description,
          tags: Array.isArray(response.data.keywords)
            ? response.data.keywords.join(", ")
            : response.data.keywords || "",
        });
        setFailed((prev) => {
          if (!prev[key]) return prev;
          const { [key]: _gone, ...rest } = prev;
          return rest;
        });
        if (!quiet) {
          toast.success(
            lang.toLowerCase() === "english"
              ? "Content rephrased successfully!"
              : `Content translated to ${lang} successfully!`
          );
        }
        return true;
      } else {
        throw new Error(response.data.error || "Rephrasing failed");
      }
    } catch (err) {
      const message = err.response?.data?.error || err.message || "Rephrasing failed";
      setFailed((prev) => ({ ...prev, [key]: message }));
      if (!quiet) toast.error(message);
      return false;
    } finally {
      setRephrasing((prev) => ({ ...prev, [key]: false }));
    }
  };

  /**
   * "Rephrase all" (user request 2026-10-13): every video × feed pair, each in
   * the language chosen on its own row (English by default), a few at a time.
   * A pair that fails keeps its text, is marked, and can be retried with its
   * own Rephrase button.
   */
  const handleRephraseAll = async () => {
    const pairs = [];
    selectedVideos.forEach((video) => feeds.forEach((feed) => pairs.push({ video, feed })));
    if (pairs.length === 0) return;
    let done = 0;
    let ok = 0;
    const failedNames = [];
    setBulk({ done: 0, total: pairs.length });
    let next = 0;
    const worker = async () => {
      while (next < pairs.length) {
        const { video, feed } = pairs[next];
        next += 1;
        // eslint-disable-next-line no-await-in-loop
        const worked = await handleRephrase(video, feed, { quiet: true });
        if (worked) ok += 1;
        else failedNames.push(`${video.unique_key} · ${feed.title || feed.name}`);
        done += 1;
        setBulk({ done, total: pairs.length });
      }
    };
    await Promise.all(Array.from({ length: Math.min(REPHRASE_ALL_CONCURRENCY, pairs.length) }, worker));
    setBulk(null);
    if (failedNames.length === 0) {
      toast.success(`All ${ok} rephrased.`);
    } else {
      // Open the failed ones so their own Rephrase button is right there.
      setExpanded((prev) => {
        const nextOpen = { ...prev };
        pairs.forEach(({ video, feed }) => {
          if (failedNames.includes(`${video.unique_key} · ${feed.title || feed.name}`)) nextOpen[pairKey(video.unique_key, feed.id)] = true;
        });
        return nextOpen;
      });
      toast.warning(
        `${ok} of ${pairs.length} rephrased. ${failedNames.length} failed — use their own Rephrase button: ${failedNames.join(", ")}`,
        { autoClose: 10000 }
      );
    }
  };

  const feedTitles = feeds.map((f) => f.title || f.name);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Step 4 — Review &amp; Submit
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Submitting sends this for approval only — nothing reaches Admin Portal until a
        CreatorVault admin approves it.
      </Typography>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }} data-testid="rephrase-prompt">
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <AutoAwesomeIcon fontSize="small" color="primary" />
            <Typography variant="body2" fontWeight={600}>
              Rephrase prompt
            </Typography>
            <Chip
              label={customPrompt ? "Edited" : "Default"}
              size="small"
              color={customPrompt ? "warning" : "default"}
              variant="outlined"
            />
          </Box>
          <Box display="flex" gap={1}>
            <Button
              size="small"
              onClick={() => changePrompt(defaultPrompt || "")}
              disabled={!customPrompt}
              data-testid="rephrase-prompt-reset"
              sx={{ textTransform: "none" }}
            >
              Reset to default
            </Button>
            <Button
              size="small"
              endIcon={promptOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setPromptOpen((o) => !o)}
              sx={{ textTransform: "none" }}
            >
              {promptOpen ? "Hide" : "Show"}
            </Button>
          </Box>
        </Box>
        <Collapse in={promptOpen}>
          <Typography variant="caption" color="text.secondary" display="block" mt={1} mb={1}>
            Used by every Rephrase and by Rephrase all below — edit it, then rephrase (or rephrase
            again) to regenerate the titles, descriptions and tags with it. Translations to other
            languages keep the built-in translate prompt. Your edit is remembered in this browser.
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={5}
            maxRows={14}
            size="small"
            value={prompt ?? ""}
            placeholder={defaultPrompt === null ? "Loading the default prompt…" : ""}
            onChange={(e) => changePrompt(e.target.value)}
            inputProps={{ "data-testid": "rephrase-prompt-text", style: { fontSize: 13 } }}
          />
        </Collapse>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" mb={0.5}>
          Target{selectedGroups.length !== 1 ? "s" : ""}:
        </Typography>
        <Box display="flex" gap={0.5} flexWrap="wrap" mb={1}>
          {selectedGroups.map((g) => (
            <Chip
              key={`${g.group_type}-${g.group_id}`}
              label={`${g.label} (${g.group_type})`}
              size="small"
              color={g.group_type === "PARTNER" ? "warning" : "primary"}
              variant="outlined"
            />
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">
          Feeds:
        </Typography>
        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
          {feedTitles.map((t, i) => (
            <Chip key={`${t}-${i}`} label={t} size="small" />
          ))}
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          mb: 2,
          borderColor: forcePush ? "warning.main" : "divider",
          bgcolor: forcePush ? "rgba(245,158,11,0.06)" : "background.paper",
        }}
      >
        <FormControlLabel
          control={
            <Switch checked={forcePush} onChange={(e) => onForcePushChange(e.target.checked)} color="warning" />
          }
          label={<Typography fontWeight={600}>Force Push</Typography>}
        />
        <Typography variant="caption" color="text.secondary" display="block">
          Publishes immediately in Admin Portal once approved, bypassing its scheduler (day/time
          window and upload gap). Off = lands in Admin Portal's normal queue for its own 5-minute
          scheduler sweep.
        </Typography>
        {forcePush && (
          <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ mt: 1 }}>
            This will go live on Admin Portal's public feed as soon as it's approved.
          </Alert>
        )}
      </Paper>

      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1} flexWrap="wrap">
        <Typography variant="body2" fontWeight={600}>
          Videos ({selectedVideos.length}) — content sent to Admin Portal, per feed
        </Typography>
        <Tooltip title="Rephrase every video's title, description and tags, each in the language picked on its own row. Any that fail keep their text and can be retried with their own Rephrase button.">
          <span>
            <Button
              size="small"
              variant="contained"
              startIcon={bulk ? <CircularProgress size={14} color="inherit" /> : <AutoAwesomeIcon fontSize="small" />}
              onClick={handleRephraseAll}
              disabled={Boolean(bulk) || selectedVideos.length === 0 || feeds.length === 0}
              data-testid="rephrase-all"
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              {bulk ? `Rephrasing ${bulk.done}/${bulk.total}…` : `Rephrase all (${selectedVideos.length * feeds.length})`}
            </Button>
          </span>
        </Tooltip>
      </Box>
      {selectedVideos.map((video) => {
        const vKey = video.unique_key;
        return (
          <Paper key={vKey} variant="outlined" sx={{ p: 1.5, mb: 1.5 }}>
            <Box mb={1}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {video.title || video.video_title || vKey}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {vKey}
              </Typography>
            </Box>

            {feeds.map((feed, idx) => {
              const key = pairKey(vKey, feed.id);
              const isOpen = !!expanded[key];
              const ov = overrides[vKey]?.[feed.id] || {};
              const lang = language[key] || "English";
              const isRephrasing = !!rephrasing[key];
              const usingTitle2 = !!(feed?.is_title_2 && video.video_title_2);
              const activeDescriptionFields = DESCRIPTION_FIELD_LABELS.filter(
                ([field]) => feed?.description_fields?.[field]
              );

              return (
                <Box key={feed.id} sx={{ pl: 1, borderLeft: "3px solid", borderColor: "divider", mt: idx > 0 ? 1.5 : 0 }}>
                  {idx > 0 && <Divider sx={{ mb: 1.5 }} />}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                    onClick={() => toggleExpanded(key)}
                  >
                    <Box minWidth={0} display="flex" alignItems="center" gap={0.5}>
                      <RssFeedIcon fontSize="small" color={failed[key] ? "error" : "action"} />
                      <Typography variant="body2" fontWeight={600} noWrap>
                        {ov.title || feed.title || feed.name}
                      </Typography>
                      {usingTitle2 && (
                        <Tooltip title="This feed is configured to use the video's Title 2 — pre-filled here since an explicit title override always wins over that setting in Admin Portal.">
                          <Chip label="Using Title 2" size="small" color="info" variant="outlined" />
                        </Tooltip>
                      )}
                    </Box>
                    <Button size="small" endIcon={isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}>
                      Edit content
                    </Button>
                  </Box>
                  <Collapse in={isOpen}>
                    <Divider sx={{ my: 1.5 }} />

                    <Box display="flex" alignItems="center" gap={1} mb={1.5} flexWrap="wrap">
                      <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>Language</InputLabel>
                        <Select
                          value={lang}
                          label="Language"
                          onChange={(e) => setLanguage((prev) => ({ ...prev, [key]: e.target.value }))}
                        >
                          {LANGUAGES.map((l) => (
                            <MenuItem key={l} value={l}>
                              {l}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      <Tooltip
                        title={
                          lang === "English"
                            ? "AI-rephrase the title/description/tags below"
                            : `AI-translate the title/description/tags below to ${lang}`
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={isRephrasing ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize="small" />}
                            onClick={() => handleRephrase(video, feed)}
                            disabled={isRephrasing}
                          >
                            {isRephrasing ? "Rephrasing…" : lang === "English" ? "Rephrase" : `Translate to ${lang}`}
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>

                    {failed[key] && (
                      <Alert severity="error" sx={{ mb: 1.5 }} data-testid={`rephrase-failed-${key}`}>
                        Rephrase failed: {failed[key]} — try this one again with its Rephrase button.
                      </Alert>
                    )}
                    <TextField
                      label={usingTitle2 ? "Title (Title 2)" : "Title"}
                      fullWidth
                      size="small"
                      value={ov.title || ""}
                      onChange={(e) => onOverrideChange(vKey, feed.id, { title: e.target.value })}
                      disabled={isRephrasing}
                      helperText={usingTitle2 ? "Pre-filled from Title 2 — this feed is configured to prefer it." : ""}
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      label="Description"
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      value={ov.description || ""}
                      onChange={(e) => onOverrideChange(vKey, feed.id, { description: e.target.value })}
                      disabled={isRephrasing}
                      sx={{ mb: 1.5 }}
                    />
                    <TextField
                      label="Tags (comma-separated)"
                      fullWidth
                      size="small"
                      value={ov.tags || ""}
                      onChange={(e) => onOverrideChange(vKey, feed.id, { tags: e.target.value })}
                      disabled={isRephrasing}
                    />

                    {activeDescriptionFields.length > 0 && (
                      <Alert
                        severity="info"
                        icon={<InfoOutlinedIcon fontSize="small" />}
                        sx={{ mt: 1.5 }}
                      >
                        <Typography variant="caption" display="block" fontWeight={600}>
                          This feed's Description fields config also appends, automatically, to
                          whatever description is sent (both Force and Queue):
                        </Typography>
                        <Box display="flex" gap={0.5} flexWrap="wrap" mt={0.5}>
                          {activeDescriptionFields.map(([field, label]) => (
                            <Chip key={field} label={label} size="small" variant="outlined" color="info" />
                          ))}
                        </Box>
                      </Alert>
                    )}
                  </Collapse>
                </Box>
              );
            })}
          </Paper>
        );
      })}
    </Box>
  );
}
